import type { ReactNode } from "react";
import { shortDay } from "../lib/offersPage";
import { DEAL_REASONS, endsSoon, type RecommendedCard, type RecommendedOffer } from "../lib/worthAdding";
import { OfferText } from "./OfferText";

export interface OfferNote {
  text: string;
  tone: string;
}

/**
 * One issuer offer on the Offers page's issuer tabs: merchant, terms, its own
 * text, when it ends, why it was picked, and a link to each card it is on.
 * `goesTo` outlines the cards the next run adds it on.
 */
export function OfferLine({
  offer, cards, today, soonDays, notes = [], goesTo, action, footer,
}: {
  offer: RecommendedOffer;
  cards: RecommendedCard[];
  today: string;
  soonDays: number;
  notes?: OfferNote[];
  goesTo?: ReadonlySet<number>;
  action?: ReactNode;
  footer?: ReactNode;
}) {
  const soon = endsSoon(offer, today, soonDays);
  const terms = offer.terms ?? offer.title;
  // One link per card: a card can list the same offer twice under two ids.
  const on = cards.filter((card) => offer.cards.some((c) => c.card_id === card.card_id));
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-1.5 px-3 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[14px] font-semibold">{offer.merchant}</span>
        {terms && <span className="text-[13px] text-neutral-300">{terms}</span>}
        <OfferText offer={offer} />
        <span className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-neutral-500">
          <span className={soon ? "text-amber-400" : ""}>
            {offer.expires_on ? `ends ${shortDay(offer.expires_on)}` : "no end date"}
          </span>
          {offer.matched.map((m) => (
            <span
              key={m}
              className={`rounded px-1.5 py-px font-mono text-[11px] ${
                DEAL_REASONS.has(m) ? "bg-green-500/10 text-green-400" : "bg-blue-400/10 text-blue-300"
              }`}
            >
              {m}
            </span>
          ))}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
          On
          {on.map((card) => {
            const goes = goesTo?.has(card.card_id) ?? false;
            return (
              <a
                key={card.card_id}
                href={card.url}
                target="_blank"
                rel="noreferrer"
                data-goes={goesTo ? String(goes) : undefined}
                className={`rounded-md bg-neutral-800 px-2 py-px text-accent hover:underline ${
                  goes ? "ring-1 ring-accent" : ""
                }`}
              >
                {goes && "→ "}{card.card_name} ↗
              </a>
            );
          })}
        </span>
        {offer.added_on && offer.added_on.length > 0 && (
          <span className="text-xs text-green-400">
            Already added on {offer.added_on.join(", ")}
          </span>
        )}
        {notes.map((note) => <span key={note.text} className={`text-xs ${note.tone}`}>{note.text}</span>)}
      </div>
      {action}
      {footer && <div className="basis-full">{footer}</div>}
    </li>
  );
}
